import {Breadcrumb, FetchedItem, FetchedSearchItem, FetchedSite, Item, PathPickerSearch, SelectableSite, TreeItem} from '../types/types';
import {PathPickerType} from '..';
import {getItemDisabled, getMimeType} from "../constants";
import {getObjectFromArray} from "../utils/arrToObj";
import { mergeFlatIntoNested } from '../../../utils/unflatten';

const moment = window.moment;

export const fetchSites = () => {
  return fetch(`${window.appContext}/proxy/alfresco/emvc/sites?&withDetail=true`, {
    credentials: 'same-origin',
  })
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data =>
      data.exist.reduce((acc: { [siteName: string]: SelectableSite }, next: FetchedSite) => ({
        ...acc,
        [next.name]: {
          label: next.title,
          value: next.name,
          nodeRef: next.siteRef,
        },
      }), {})
    )
    .catch(reason => {
      throw reason;
    });
};

export const fetchFolderChildren = ({
  folderNodeRef,
  filter,
  type,
  page = 1,
  sort = true,
  sortByValue = 'cm:name',
  pageSize = 50,
  pickerName,
}: PathPickerSearch) => {
  return fetch(
    `${window.appContext}/proxy/alfresco/emvc/documents/children?${folderNodeRef ? `parentNodeRef=${folderNodeRef}` : "" }${filter ? `&filter=${filter}` : ""}&skipCount=${(page - 1) * pageSize}&maxItems=${pageSize}&searchFolders=${type === PathPickerType.FOLDER ? 'FOLDERS' : 'ALL'}&orderBy=isFolder DESC,${sortByValue} ${sort ? 'ASC' : 'DESC'}`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => {
      const total = data.total;
      const isSiteRoot = false;
      return {
        items: data.items.map((item: any) => ({
          name: item.name,
          nodeRef: item.nodeRef,
          type: item.folder ? PathPickerType.FOLDER : PathPickerType.FILE,
          properties:
            {
              ...item.properties,
              'cm:created': { iso8601: item.createdAt },
              'cm:modified': { iso8601: item.modifiedAt },
              'cm:modifier': {
                firstName: item.modifiedByUser.displayName,
                lastName: item.modifiedByUser.displayName,
                displayName: item.modifiedByUser.displayName,
                userName: item.modifiedByUser.id,
              },
            } || {},
          indicators: [],
          mimetype: 'Default',
          originalMimeType: item?.mimeType,
          workingCopy: null,
          version: '1.0',
          path: item.path.replace("/Company Home/Sites/", ""),
          isLocked: item?.isLockedDoc ?? false,
          disabled:
            item.folder ?
              type === PathPickerType.FILE ?
                false
                :
                getItemDisabled(pickerName, item.owner, item.highestPermission)
            :
              getItemDisabled(pickerName, item.owner, item.highestPermission),
        })),
        total,
        isSiteRoot,
      };
    })
    .catch(reason => {
      throw reason;
    });
};

export const fetchTreeChildren = (folderNodeRef: string) => {
  const modifiedNodeRef: string = folderNodeRef.replace(':/', '');

  return fetch(
    `${window.appContext}/proxy/alfresco/slingshot/doclib/treenode/node/${modifiedNodeRef}?children=true`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => ({
      [data.parent.nodeRef]: data.items.map((item: TreeItem) => ({
        name: item.name,
        nodeRef: item.nodeRef,
        hasChildren: item.hasChildren,
      })),
    }))
    .catch(reason => {
      throw reason;
    });
};

export const search = (
  query: string,
  rootNodeRef: string,
  type: PathPickerType,
  skipCount: number = 0,
  maxItems: number = 10,
) => {
  const rootNodeParam: string = rootNodeRef ? `&rootNodeId=${rootNodeRef.replace('workspace://SpacesStore/', '')}` : '';
  const nodeTypeParam: string = type === PathPickerType.FOLDER ? '&nodeType=cm:folder&nodeType=ctfd:baseFolder' : '';

  return fetch(
    `${window.appContext}/proxy/alfresco-api/-default-/public/alfresco/versions/1/queries/nodes?term=${query}*&skipCount=${skipCount}&maxItems=${maxItems}&include=path,allowableOperations${rootNodeParam}${nodeTypeParam}`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(async data => {
      //Issue #1: Due to unknown reason only on production documents which have comments are returned with isFolder: true,
      //nodeType: 'fm:forum' and ' diskuse' postfix in the name. If searching a file, it duplicates documents with
      //comments. To avoid their incorrect appearance in search results the check nodeType !== 'fm:forum' is applied
      //below.
      //Issue #2: In case, when the user has permissions for the folder, but not for some part of its path, its path
      //property has empty object value ({}), which therefore doesn't have elements property. In this case we need to
      //leave path empty.
      return {
        items: data.list.entries.reduce((acc: Item[], next: FetchedSearchItem) => {
          const { path, name, id, isFolder, nodeType, content, allowableOperations } = next.entry;

          if (nodeType !== 'fm:forum') {
            let newPath: Breadcrumb[] = [];

            if (path?.elements && path.elements.length >= 2 && path.elements[3]?.name === 'documentLibrary') {
              newPath = path.elements.slice(2).map((item: { name: string; id: string; }) => ({
                name: item.name,
                nodeRef: `workspace://SpacesStore/${item.id}`,
              }));
            }

            acc.push({
              name,
              nodeRef: `workspace://SpacesStore/${id}`,
              type: isFolder ? PathPickerType.FOLDER : PathPickerType.FILE,
              path: newPath,
              mimetype: isFolder ? 'folder' : getMimeType(content?.mimeType || ''),
              originalMimeType: content?.mimeType ?? '',
              disabled: isFolder ? !Boolean(allowableOperations) : false,
            });
          }
          return acc;
        }, []),
        total: data?.list?.pagination?.totalItems || 0,
      }
    })
    .catch(reason => {
      throw reason;
    });
};

export const fetchItems = async (nodeRefs: string[], hidden: string[] = []) => {
  const promises = nodeRefs.map(item => {
    const itemId: string = item.replace('workspace://SpacesStore/', '');

    return fetch(
      `${window.appContext}/proxy/alfresco-api/-default-/public/alfresco/versions/1/nodes/${itemId}?include=path`
    ).then(data => data.json());
  });

  const responses = await Promise.all(promises);

  return responses.map(item => ({
    name: item.entry.name,
    nodeRef: `workspace://SpacesStore/${item.entry.id}`,
    type: item.entry.isFolder ? PathPickerType.FOLDER : PathPickerType.FILE,
    path:
      item.entry.path?.elements?.slice(2).map((item: { name: string; id: string; }) => ({
        name: item.name === item.id ? '' : item.name,
        nodeRef: `workspace://SpacesStore/${item.id}`,
      })) || [],
    deletable: !hidden.includes(`workspace://SpacesStore/${item.entry.id}`),
    message: '',
  }));
};

export const fetchBreadcrumbs = (folderNodeRef: string, parentPath: boolean = false) => {
  const itemId: string = folderNodeRef.replace('workspace://SpacesStore/', '');

  return fetch(
    `${window.appContext}/proxy/alfresco-api/-default-/public/alfresco/versions/1/nodes/${itemId}?include=path`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => {
      const path = data.entry.path?.elements.slice(2).map((item: any) => ({
          name: item.name,
          nodeRef: `workspace://SpacesStore/${item.id}`,
        })) || [];

      return parentPath ? path : [...path, { name: data.entry.name, nodeRef: `workspace://SpacesStore/${data.entry.id}` }];
    })
    .catch(reason => {
      throw reason;
    });
};

export const fetchChildrenByFilter = (
  site: string,
  filter: string,
  type: PathPickerType,
  page = 1,
  sort = true,
  sortByValue = 'cm:name',
  pageSize = 50,
) => {
  return fetch(
    `${
      window.appContext
    }/service/components/documentlibrary/data/doclist/${type === PathPickerType.FOLDER ? 'folders' : 'all'}/site/${site}/documentLibrary?filter=${filter}&size=${pageSize}&pos=${page}&sortAsc=${sort}&sortField=${sortByValue}&view=browse&${moment().unix()}`,
    {
      credentials: 'same-origin',
    }
  )
    .then(response => {
      if (response.status !== 200) throw new Error(response.statusText);
      return response.json();
    })
    .then(data => {
      const total = type === PathPickerType.FOLDER ? data?.metadata?.itemCounts?.folders || 0 : data.totalRecords;

      return ({
        items: data.items.map((item: FetchedItem) => ({
          name: item.displayName,
          nodeRef: item.nodeRef,
          type: item.node.isContainer ? PathPickerType.FOLDER : PathPickerType.FILE,
          properties: item.node.properties || {},
          indicators: item.indicators,
          mimetype: getMimeType(item.node.mimetype),
          originalMimeType: item.node.mimetype,
          workingCopy: item.workingCopy ? item.workingCopy : null,
          version: item.version,
          isLocked: item.node.isLocked,
        })),
        total,
      });
    })
    .catch(reason => {
      throw reason;
    });
};

export const savePreferences = (preferences: any) => {
  return fetch(`${window.appContext}/proxy/alfresco/emvc/userpref/set`, {
    credentials: 'same-origin',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      [window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
    },
    body: JSON.stringify(preferences),
  })
    .then(response => {
      if (response.status !== 200) {
        throw new Error(response.statusText);
      }
    })
    .catch(reason => {
      throw new Error(reason);
    });
};

export const fetchPreferences = (prefixKeys = {"prefixKeys": ["org"]}, exactKeys = {}) => {
  return fetch(`${window.appContext}/proxy/alfresco/emvc/userpref/get`, {
    credentials: 'same-origin',
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
		},
		body: JSON.stringify({
			...prefixKeys,
			...exactKeys,
		})
  })
    .then(response => {
      if (response.status !== 200) {
        throw new Error(response.statusText);
      }
			return response.json().then(json => {
				return mergeFlatIntoNested(json.data);
			})
    })
    .then(preferences => {
      return {
        favouriteDocuments: getObjectFromArray(preferences.org?.alfresco?.share?.documents?.favourites?.split(',')) || {},
        favouriteFolders: getObjectFromArray(preferences.org?.alfresco?.share?.folders?.favourites?.split(',')) || {},
      };
    })
    .catch(reason => {
      throw new Error(reason);
    });
};